import { translate } from '@components/translation/translation';
import { ProjectType } from '@lib/api_ts/projectApi';
import React, { Dispatch, SetStateAction } from 'react';
import { Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import { FieldType } from './RegistryForm';
import validateField from './utils/validateField';
import { dateFormatter } from '@lib/helpers/formatting';

interface RenderInputProps {
    field: FieldType
    projectToSave: Partial<ProjectType> | undefined;
    setProjectToSave: Dispatch<SetStateAction<Partial<ProjectType> | undefined>>;
    getIsInvalid: (fieldName: string) => boolean;
    setInvalidFields: Dispatch<SetStateAction<string[]>>;
    isDirty: boolean;
    setIsDirty: Dispatch<SetStateAction<boolean>>;
}

const RenderInput: React.FC<RenderInputProps> = (
    { field, projectToSave, setProjectToSave, getIsInvalid, setInvalidFields, isDirty, setIsDirty }
) => {

    const isSingleSelectDisabled = field.isDisabled ||
        (projectToSave?.editables ? !projectToSave?.editables?.company : false);

    const fieldValue = projectToSave?.[field.name] ?? '';

    const singleSelectValue = field.options?.find(
        option => option.value === String(projectToSave?.[field.name])
    );

    const multiSelectValue = field.options?.filter(option => {
        return Array.isArray(projectToSave?.[field.name]) &&
            projectToSave[field.name].map(String).includes(option.value);
    });

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value;
        if (!isDirty) setIsDirty(true);

        setInvalidFields(prevInvalidFields => {
            const error = validateField(field, value, projectToSave);

            if (error) {
                return [...new Set([...prevInvalidFields, field.name])];
            }

            return prevInvalidFields.filter(prevInvalidField => prevInvalidField !== field.name);

        });

        setProjectToSave({
            ...projectToSave,
            [field.name]: value
        });
    }
    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProjectToSave({
            ...projectToSave,
            [field.name]: value,
            cup: '' // Reset cup when radio changes
        });
    }

    const handleSingleSelectChange = (newValue: { value: string }) => {
        if (!isDirty) setIsDirty(true);

        setInvalidFields(prevInvalidFields => {

            const error = validateField(field, newValue.value, projectToSave);
            if (error) {
                return [...new Set([...prevInvalidFields, field.name])];
            }

            return prevInvalidFields.filter(prevInvalidField => prevInvalidField !== field.name);
        });
        setProjectToSave({
            ...projectToSave,
            [field.name]: newValue.value
        });
    }
    const handleMultiSelectChange = (newValue: { value: string }[]) => {
        const extractedValues = newValue.map(option => option.value);
        if (!isDirty) setIsDirty(true);

        setInvalidFields(prevInvalidFields => {
            const error = validateField(field, extractedValues, projectToSave);
            if (error) {
                return [...new Set([...prevInvalidFields, field.name])];
            }
            return prevInvalidFields.filter(name => name !== field.name);
        });

        setProjectToSave({
            ...projectToSave,
            [field.name]: extractedValues
        });
    };

    const handleRadioInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, fieldName: string) => {
        const value = event.target.value;
        if (!isDirty) setIsDirty(true);

        setInvalidFields(prevInvalidFields => {

            const error = validateField(field, value, projectToSave);
            if (error) {
                return [...new Set([...prevInvalidFields, fieldName])];
            }
            return prevInvalidFields.filter(name => name !== fieldName);
        });

        setProjectToSave(prev => ({
            ...prev,
            [fieldName]: value
        }));
    }

    const renderInput = (field: FieldType) => {
        switch (field.type) {
            case 'text':
                return <Form.Control
                    type={field.type}
                    name={field.name}
                    onChange={handleOnChange}
                    required={field.required}
                    value={
                        fieldValue
                    }
                    disabled={field.isDisabled}
                    isInvalid={getIsInvalid(field.name)}
                />;
            case 'date':
                return <Form.Control
                    type={field.type}
                    name={field.name}
                    onChange={handleOnChange}
                    required={field.required}
                    value={
                        dateFormatter(fieldValue, 'YYYY-MM-DD') || ''
                    }
                    disabled={field.isDisabled}
                    isInvalid={getIsInvalid(field.name)}
                />;
            case 'select':
                return <>
                    <Select
                        components={{
                            IndicatorSeparator: () => null,
                        }}
                        isLoading={field.isLoading}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        name="companyId"
                        placeholder={
                            !isSingleSelectDisabled || field.isDisabled ? translate('common.placeholder.select', 'Seleziona...') : ""}
                        value={
                            field.isMulti
                                ? multiSelectValue
                                : singleSelectValue
                        }
                        onChange={
                            field.isMulti ?
                                handleMultiSelectChange :
                                handleSingleSelectChange
                        }
                        options={field.options}
                        isDisabled={
                            field.isMulti ?
                                field.isDisabled :
                                isSingleSelectDisabled
                        }

                        isMulti={field.isMulti}

                        classNames={{
                            control: () => getIsInvalid(field.name) ? 'form-control is-invalid' : '',
                        }}
                    />
                    {getIsInvalid(field.name) ?
                        <div className="invalid-feedback d-block">Campo obbligatorio</div> : null}
                </>
            case 'textarea':
                return <Form.Control
                    as="textarea"
                    rows={3}
                    name={field.name}
                    onChange={handleOnChange}
                    required={field.required}
                    value={
                        fieldValue
                    }
                    isInvalid={getIsInvalid(field.name)}
                    disabled={field.isDisabled}
                />
            case 'radio':
                return <Form.Group style={{ display: "grid", gap: "1.5rem", marginTop: "0.5rem" }}>
                    {field.options.map(option => (
                        <Form.Group key={option.value} as={Col} sm={6} style={{ display: 'flex', gap: '0.5rem' }}>
                            <Form.Check
                                type="radio"
                                name={field.name}
                                // label={option.label}
                                value={option.value}
                                checked={projectToSave?.[field.name] === option.value}
                                onChange={handleRadioChange}
                                required={field.required}
                                isInvalid={getIsInvalid(field.name)}
                                disabled={field.isDisabled}
                                style={{ marginTop: '0.5rem' }}
                                className='custom-radio'
                            />
                            {projectToSave?.[field.name] === option.value ?
                                <Form.Group style={{ width: '100%' }}
                                >
                                    <Form.Control
                                        type="text"
                                        placeholder={
                                            `Inserisci ${option.label}...`
                                        }
                                        name={option.fieldName}
                                        onChange={
                                            (event) => handleRadioInputChange(event, option.fieldName)
                                        }
                                        required={field.required}
                                        value={projectToSave?.[option.fieldName] || ''}
                                        isInvalid={getIsInvalid(option.fieldName)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Campo obbligatorio.
                                    </Form.Control.Feedback>
                                </Form.Group>
                                : <Form.Control
                                    type="text"
                                    disabled
                                    aria-hidden="true"
                                    placeholder={
                                        `Inserisci ${option.label}...`
                                    } />}
                        </Form.Group>
                    ))}
                </Form.Group>
        }
    }


    if (field.hidden) return null;
    return <Form.Group key={field.name} as={Col}>
        <Form.Label>
            {field.label}
            <span className="text-danger">{field.required ? '*' : ''}</span>
        </Form.Label>
        {
            renderInput(field)
        }
        <Form.Control.Feedback type="invalid">
            {validateField(field, projectToSave?.[field.name], projectToSave)}
        </Form.Control.Feedback>
    </Form.Group>
}

export default RenderInput